import { useCallback, useEffect, useMemo, useRef } from 'react'
import AddPendingVisitSceneChooser from '../../components/visit-creation/AddPendingVisitSceneChooser'
import Scenes from '../../util/Scenes'
import { ADD_PENDING_VISIT, createMedicalPath, createWithQuery, DASHBOARD, MEDICAL_WIZARD } from '../../routes/paths'
import { Alert, ConfirmationAlert } from 'ui-components-web'
import VisitScheduleType from '../../util/visit-schedule-types'
import VisitTypes from '../../util/visit-types'
import useAlert from '../../hooks/useAlert'
import NewVisitSchema from '../../schemas/NewVisitSchema'
import AddVisitPageLayout from '../../layouts/AddVisitPageLayout'
import NoProvidersAvailable from '../../components/visit-creation/NoProvidersAvailable'
import TestIds from '../../util/TestIds'
import * as APM from 'util/apm'
import PendingVisit from '../../types/visit/pendingVisit'
import type { AnyAction, Dispatch } from 'redux'
import Member, { Phone, Service } from '../../types/member/member'
import Provider from '../../types/provider/provider'
import { useHistory } from 'react-router'
import { useQuery } from 'hooks/useQuery'
import NewVisitEntrySchema from 'schemas/NewVisitEntrySchema'
import AddPendingVisitRedirectModal from 'components/visit-creation/AddPendingVisitRedirectModal'
import { MemberSelectSchema } from 'schemas/MemberSelectSchema'
import { toast } from 'util/toast'
import { getMemberHistoryStatus } from 'redux/selectors/medical-history'
import { useSelector } from 'react-redux'
import { MEMBER_ID } from 'util/session-constants'
import { ModalRef } from '@ui-components-3/ui/lib/components/Modal'
import { isUSLocation } from 'util/visit-location'
import { isMedicalWizardNeeded } from 'util/medical-wizard'

export const Steps = {
  addService: 0,
  memberSelect: 1,
  location: 2,
  locationState: 3,
  locationCountry: 4,
  visitType: 5,
  reason: 6,
  modality: 7,
  provider: 8,
  scheduling: 9,
  pharmacy: 10,
  payment: 11,
  confirmation: 12,
  noAvailableProviders: 13,
} as const

const SCENE_STEPS = {
  [Steps.addService]: Scenes.newVisit.addService,
  [Steps.memberSelect]: Scenes.newVisit.memberSelect,
  [Steps.location]: Scenes.newVisit.location,
  [Steps.locationState]: Scenes.newVisit.locationState,
  [Steps.locationCountry]: Scenes.newVisit.locationCountry,
  [Steps.visitType]: Scenes.newVisit.visitType,
  [Steps.reason]: Scenes.newVisit.reason,
  // Property 'preference' commented out in 'Steps'
  // @ts-ignore
  [Steps.preference]: Scenes.newVisit.preference,
  [Steps.modality]: Scenes.newVisit.modality,
  [Steps.provider]: Scenes.newVisit.providerList,
  [Steps.scheduling]: Scenes.newVisit.scheduling,
  [Steps.pharmacy]: Scenes.newVisit.pharmacy,
  [Steps.payment]: Scenes.newVisit.payment,
  [Steps.confirmation]: Scenes.newVisit.confirmation,
  [Steps.noAvailableProviders]: Scenes.newVisit.noAvailableProviders,
} as const

export const isBeforeVisitTypeSelection = (step: number) => step < Steps.visitType
export const isEntryStep = (step: number) => step === Steps.memberSelect
const isLocationStep = (step: number) => !isEntryStep(step) && step < Steps.visitType

const isPharmacyReqiured = (visitType) => visitType === VisitTypes.psychiatry || visitType === VisitTypes.medical

type AddPendingVisitPageProps = {
  member: Member
  provider: Provider
  memberId: string | null
  pendingVisit: PendingVisit
  isLoading: boolean
  hasServices: boolean
  addPhoneNumber: (phone: Phone) => Promise<
    | ((
        dispatch: Dispatch,
        getState: () => any,
      ) => Promise<{
        success: boolean
        data: Member | null
      }>)
    | null
  >
  clearPayload: () => void
  updatePayload: (payload: any) => void
  createPendingVisit: (payload: any) => Promise<
    (dispatch: Dispatch) => Promise<{
      success: boolean
      data: null
    }>
  >
  checkProviderAvailability: (
    location: string,
    visitType: string,
  ) => Promise<
    (
      dispatch: Dispatch,
      getState: any,
    ) => Promise<{
      success: boolean
      count: number
    }>
  >
  fetchVisitCount: (visitType: string) => Promise<
    (dispatch: Dispatch) => Promise<{
      success: boolean
      count: null
    }>
  >
  selectedService: Service
  fetchAvailableProviders: (filters: any) => Promise<
    | ((dispatch: Dispatch) => Promise<{
        success: boolean
        data: null
      }>)
    | null
  >
  fetchProviderAvailability: () => Promise<
    | ((dispatch: any) => Promise<{
        success: boolean
        data: null
      }>)
    | null
  >
  addServiceCode: (accessCode: string) => Promise<(dispatch: Dispatch<AnyAction>) => Promise<{ success: boolean }>>
  pendingScheduledVisits: any
  userMemberId: string
}

const AddPendingVisitPage = (props: AddPendingVisitPageProps) => {
  const {
    pendingVisit,
    clearPayload,
    updatePayload,
    createPendingVisit,
    fetchVisitCount,
    selectedService,
    addServiceCode,
    hasServices,
  } = props
  const query = useQuery(['step', 'redirect'])
  const redirectModalRef = useRef<ModalRef | null>(null)
  const { alert, setAlert, resetAlert } = useAlert()
  const memberId = sessionStorage.getItem(MEMBER_ID)
  const medicalHistoryStatus = useSelector(getMemberHistoryStatus(memberId))
  const medicalHistoryCompleted = useMemo(
    () => medicalHistoryStatus && !Object.values(medicalHistoryStatus).filter((status) => !status).length,
    [medicalHistoryStatus],
  )
  const history = useHistory()

  const step = useMemo(() => {
    const step = Number(query?.step)
    if (Number.isNaN(step)) return Steps.memberSelect
    return step
  }, [query?.step])

  const scene = useMemo(() => {
    if (!step && step !== Steps.addService) {
      return Scenes.newVisit.memberSelect
    }

    return SCENE_STEPS[step]
  }, [step])

  const navigateHome = useCallback(() => {
    resetAlert()
    history.push(DASHBOARD)
  }, [history, resetAlert])

  const startOver = useCallback(() => {
    resetAlert()
    history.push(createWithQuery(ADD_PENDING_VISIT, { step: Steps.memberSelect }))
  }, [history, resetAlert])

  useEffect(() => {
    if (query?.redirect) {
      redirectModalRef.current?.open()
    }
  }, [query])

  useEffect(() => {
    const isEntryInvalid = !isEntryStep(step) && !MemberSelectSchema.isValidSync(pendingVisit)
    const isLocationInvalid = !isLocationStep(step) && !NewVisitEntrySchema.isValidSync(pendingVisit)
    if (isEntryInvalid || isLocationInvalid) {
      if (hasServices) {
        history.push(
          createWithQuery(ADD_PENDING_VISIT, {
            step: Steps.memberSelect,
            redirect: query?.redirect ? query?.redirect : null,
          }),
        )
      } else {
        history.push(
          createWithQuery(ADD_PENDING_VISIT, {
            step: Steps.addService,
            redirect: query?.redirect ? query?.redirect : null,
          }),
        )
      }
    }
  }, [history, pendingVisit, step, hasServices])

  useEffect(() => {
    if (scene === Scenes.newVisit.noAvailableProviders) {
      setAlert({
        visible: true,
        body: <NoProvidersAvailable />,
        customButtons: (
          <div className={'alert-right-aligned-container'}>
            <button
              className="btn btn-primary-outlined"
              onClick={navigateHome}
              data-testid={TestIds.newVisit.button.goHome}
              type="button"
            >
              OK
            </button>
          </div>
        ),
        showButtons: false,
        showCancel: false,
        onCancel: startOver,
        onConfirm: navigateHome,
      })
    }
  }, [scene])

  const getNextStep = useCallback(
    (overLimit = false) => {
      // TODO preferences -> replace first cond with step > Steps.preferences
      if (step > Steps.reason && step < Steps.pharmacy) {
        if (pendingVisit.scheduledType === VisitScheduleType.scheduled) {
          if (step < Steps.scheduling) {
            return step + 1
          }
        }

        if (isPharmacyReqiured(pendingVisit.visitType)) {
          return Steps.pharmacy
        } else if (!pendingVisit.price && !overLimit) {
          return Steps.confirmation // skip payment screen
        } else {
          return Steps.payment // no pharmacy selection, but show payment screen
        }
      }
      if (step === Steps.pharmacy && !pendingVisit.price) {
        return Steps.confirmation // skip the payment screen
      }
      if (step === Steps.reason && pendingVisit.scheduledType !== VisitScheduleType.now) {
        return Steps.modality
      }
      return step + 1
    },
    [step, pendingVisit],
  )

  const handleCancel = useCallback(() => {
    clearPayload()
    history.push(DASHBOARD)
  }, [clearPayload, history])

  const navigateToMedicalWizard = () => {
    history.push(createMedicalPath(MEDICAL_WIZARD, memberId))
  }

  const handleSceneSubmit = useCallback(
    async (values: any, _: any) => {
      try {
        if (Scenes.newVisit.confirmation === scene) {
          const payload = NewVisitSchema.cast(values)
          const promise = createPendingVisit(payload)
          // @ts-ignore
          const { success } = await promise
          if (success) {
            toast.success(
              <p role="alert" style={{ lineHeight: '18px', margin: '5px' }}>{`Visit has been created successfully`}</p>,
            )
            history.push(DASHBOARD)
            clearPayload()
          } else {
            console.error('error adding visit')
          }
        } else if (Scenes.newVisit.scheduling === scene || Scenes.newVisit.pharmacy === scene) {
          let isOverLimit = false
          if (selectedService.limitMember && selectedService.overLimitFee) {
            // @ts-ignore
            const { count, success } = await fetchVisitCount(selectedService.serviceType)
            if (success && count >= selectedService.limitMember) isOverLimit = true
          }
          if (isOverLimit) {
            await updatePayload({
              ...values,
              price: selectedService.overLimitFee,
            })
          } else {
            await updatePayload(values)
          }
          history.push(
            createWithQuery(ADD_PENDING_VISIT, {
              step: getNextStep(isOverLimit),
            }),
            { isOverLimit },
          )
        } else if (Scenes.newVisit.location === scene) {
          const newValues = {
            location: '',
            locationCountry: values.isOutsideUS === 'true' ? '' : 'US',
            visitType: '',
            scheduledType: '',
          }
          await updatePayload(newValues)
          history.push(
            createWithQuery(ADD_PENDING_VISIT, {
              step: isUSLocation(newValues.locationCountry) ? Steps.locationState : Steps.locationCountry,
            }),
          )
        } else if (Scenes.newVisit.locationState === scene || Scenes.newVisit.locationCountry === scene) {
          const newValues = {
            location: Scenes.newVisit.locationState === scene ? values.location : '',
            locationCountry: Scenes.newVisit.locationCountry === scene ? values.locationCountry : 'US',
          }
          await updatePayload(newValues)
          history.push(
            createWithQuery(ADD_PENDING_VISIT, {
              step: Steps.visitType,
            }),
          )
        } else if (isMedicalWizardNeeded(values, scene, medicalHistoryCompleted)) {
          await updatePayload(values)
          navigateToMedicalWizard()
        } else if (Scenes.newVisit.addService === scene) {
          await addServiceCode(values.code)
          history.push(
            createWithQuery(ADD_PENDING_VISIT, {
              step: getNextStep(),
            }),
          )
        } else {
          await updatePayload(values)
          history.push(
            createWithQuery(ADD_PENDING_VISIT, {
              step: getNextStep(),
            }),
          )
        }
      } catch (e) {
        console.error(e)
        APM.captureException(e)
      }
    },
    [scene, updatePayload, getNextStep, step, history],
  )

  const handleCancelClick = useCallback(() => {
    setAlert({
      type: 'custom',
      visible: true,
      body: 'Are you sure you want to cancel this visit?',
      onYes: handleCancel,
      onNo: resetAlert,
      yesContent: 'YES, CANCEL VISIT',
      yesAriaLabel: 'Yes, cancel visit',
      noContent: 'NO',
      noAriaLabel: 'No, do not cancel visit',
      customNoBtnClass: 'alert-link-no alert-link',
      customYesBtnClass: 'alert-link-yes alert-link',
      bodyTestId: TestIds.newVisit.view.cancelVisit,
      buttonsTestId: TestIds.newVisit.view.cancelVisitBtn,
    })
  }, [handleCancel, resetAlert, setAlert])

  return (
    <AddVisitPageLayout step={step}>
      <AddPendingVisitSceneChooser
        {...props}
        scene={scene}
        onCancel={handleCancelClick}
        onSubmit={handleSceneSubmit}
        clearPayload={clearPayload}
      />
      <ConfirmationAlert {...alert} visible={alert.visible && alert.type === 'custom'} />
      <Alert {...alert} isAlert={alert.visible && alert.type !== 'custom'} />
      {query?.redirect && <AddPendingVisitRedirectModal ref={redirectModalRef} queryValue={query.redirect} />}
    </AddVisitPageLayout>
  )
}

export default AddPendingVisitPage
